import type {
    PortfolioBacktest,
    PortfolioBacktestComponentWindow,
    PortfolioBacktestRiskStatisticsRow,
    PortfolioWalkForwardBoundary
} from '@einhaus/project314-types';

type RiskPeriod = PortfolioBacktestRiskStatisticsRow['period'];

export interface WalkForwardBoundaryCopy {
    option: string;
    before: string;
    after: string;
    riskPeriods: readonly [before: RiskPeriod, after: RiskPeriod];
}

/** The saved split is a launch-time reporting choice; the component boundary follows the run's own components. */
export const WALK_FORWARD_BOUNDARY_COPY: Record<PortfolioWalkForwardBoundary, WalkForwardBoundaryCopy> = {
    savedTrainingEnd: {
        option: 'Saved training end',
        before: 'Before saved split',
        after: 'After saved split',
        riskPeriods: ['training', 'walkForward']
    },
    componentTrainingEnd: {
        option: 'Post-training (latest component training end)',
        before: 'Component development (in-sample)',
        after: 'Post-training',
        riskPeriods: ['componentTraining', 'postComponentTraining']
    }
};

const RISK_WINDOW_LABELS: Record<RiskPeriod, string> = {
    overall: 'Full range',
    training: 'Before saved split',
    walkForward: 'After saved split',
    componentTraining: 'Component development',
    postComponentTraining: 'Post-training'
};

export const riskWindowLabel = (period: RiskPeriod): string => RISK_WINDOW_LABELS[period];

/** The full-range row followed by the selected boundary's in-sample and walk-forward rows, in that order. */
export function selectRiskStatisticsForBoundary<Row extends Pick<PortfolioBacktestRiskStatisticsRow, 'period'>>(
    rows: readonly Row[],
    boundary: PortfolioWalkForwardBoundary | null
): Row[] {
    const periods: RiskPeriod[] = ['overall', ...(boundary ? WALK_FORWARD_BOUNDARY_COPY[boundary].riskPeriods : [])];
    return periods.flatMap((period) => rows.filter((row) => row.period === period));
}

/** Eastern `YYYY-MM-DD HH:mm:ss` values compare lexicographically; midnight values display as dates. */
export const formatEasternBoundary = (dateTime: string): string => (dateTime.endsWith(' 00:00:00') ? dateTime.slice(0, 10) : dateTime);

/** Qualifies the saved evaluation window; passing numerical criteria does not establish an untouched holdout. */
export function portfolioEvaluationWindowWarning(
    window: Pick<
        PortfolioBacktest,
        'trainingEndDateTime' | 'componentTrainingEndDateTime' | 'componentValidationEndDateTime' | 'componentWindowsSource'
    >
): { label: string; reason: string } | null {
    const { trainingEndDateTime, componentTrainingEndDateTime, componentValidationEndDateTime, componentWindowsSource } = window;
    if (trainingEndDateTime && componentTrainingEndDateTime && trainingEndDateTime < componentTrainingEndDateTime)
        return {
            label: 'In development',
            reason: `The saved split (${formatEasternBoundary(trainingEndDateTime)}) precedes the component training end (${formatEasternBoundary(componentTrainingEndDateTime)}). This evaluation includes development data.`
        };
    // Validation ends are inclusive; an entry exactly at that endpoint is still previously evaluated data.
    if (trainingEndDateTime && componentValidationEndDateTime && trainingEndDateTime <= componentValidationEndDateTime)
        return {
            label: 'Previously evaluated',
            reason: `Component validation windows run through ${formatEasternBoundary(componentValidationEndDateTime)}. This evaluation includes data already viewed during component selection.`
        };
    if (!trainingEndDateTime || !componentTrainingEndDateTime || componentWindowsSource !== 'appliedChildren')
        return {
            label: 'Window unverified',
            reason: 'Applied component training windows are not resolved for this evaluation. Its saved split does not establish an untouched holdout.'
        };
    return null;
}

export const isSavedSplitInsideComponentDevelopment = (backtest: {
    trainingEndDateTime?: string | null | undefined;
    componentTrainingEndDateTime?: string | null | undefined;
}): boolean =>
    Boolean(
        backtest.trainingEndDateTime &&
        backtest.componentTrainingEndDateTime &&
        backtest.trainingEndDateTime < backtest.componentTrainingEndDateTime
    );

/** Data through this inclusive endpoint was already evaluated by component selection, so it is not an untouched holdout. */
export const viewedValidationEndAfter = (
    walkForwardStartDateTime: string | null,
    componentValidationEndDateTime: string | null | undefined
): string | null =>
    walkForwardStartDateTime && componentValidationEndDateTime && componentValidationEndDateTime >= walkForwardStartDateTime
        ? componentValidationEndDateTime
        : null;

const COMPONENT_TYPE_LABELS: Record<PortfolioBacktestComponentWindow['componentType'], [singular: string, plural: string]> = {
    scorecard: ['Scorecard', 'scorecards'],
    exitCandidate: ['Exit candidate', 'exit candidates'],
    screenerRule: ['Screener rule', 'screener rules'],
    sourceExperiment: ['Source experiment', 'source experiments'],
    entityList: ['Entity list', 'entity lists'],
    optionBuyingStrategy: ['Option buying strategy', 'option buying strategies']
};

export const componentLabel = (window: PortfolioBacktestComponentWindow): string =>
    `${COMPONENT_TYPE_LABELS[window.componentType][0]} ${window.componentId}`;

export function windowSourceLabel(window: PortfolioBacktestComponentWindow): string {
    switch (window.windowSource) {
        case 'scorecardBaselineExperiment':
            return `Baseline experiment ${window.windowSourceId}`;
        case 'aeroJob':
            return `AERO job ${window.windowSourceId}`;
        case 'screenerOptimizerJob':
            return `Screener optimizer job ${window.windowSourceId}`;
        case 'experiment':
            return `Experiment ${window.windowSourceId}`;
        case 'unrecorded':
            return 'No recorded window';
    }
}

/** e.g. "2 option buying strategies, 1 entity list" for selections whose development data is unknown. */
export function summarizeUnrecordedComponents(windows: readonly PortfolioBacktestComponentWindow[]): string | null {
    const counts = new Map<PortfolioBacktestComponentWindow['componentType'], number>();
    for (const window of windows)
        if (window.windowSource === 'unrecorded') counts.set(window.componentType, (counts.get(window.componentType) ?? 0) + 1);
    if (!counts.size) return null;
    return [...counts]
        .map(([type, count]) => `${count} ${count === 1 ? COMPONENT_TYPE_LABELS[type][0].toLowerCase() : COMPONENT_TYPE_LABELS[type][1]}`)
        .join(', ');
}
